import data from "./route-lastmod.json";

const routes = data.routes as Record<string, string>;
const libs = data.libs as Record<string, string | null>;
const posts = (data.posts ?? {}) as Record<string, string>;
const esPosts = (data.esPosts ?? {}) as Record<string, string>;
export const inlineUpdatedRoutes: readonly string[] =
  (data as typeof data & { inlineUpdatedRoutes?: string[] }).inlineUpdatedRoutes ?? [];

const newer = (a?: string | null, b?: string | null) =>
  a && b ? (a > b ? a : b) : (a ?? b ?? undefined);

/**
 * Last content-change date (YYYY-MM-DD) for a URL path, derived from the git
 * history of the page's own source file (and, for dynamic pages, the content
 * library that supplies its text). Returns undefined when unknown, in which
 * case callers should omit <lastmod> rather than invent one.
 */
export function lastmodFor(path: string): string | undefined {
  const clean = path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;

  if (clean.startsWith("/results/story/")) {
    return newer(routes["/results/story"], libs.stories) ?? undefined;
  }
  if (clean.startsWith("/es/antes-y-despues/historia/")) {
    return newer(routes["/es/antes-y-despues"], libs.esStories) ?? undefined;
  }
  if (clean.startsWith("/dr-fitzer-dissects-the-news/")) {
    return posts[clean] ?? undefined;
  }
  if (clean.startsWith("/es/el-doctor-analiza-las-noticias/")) {
    return esPosts[clean] ?? undefined;
  }
  return routes[clean];
}

/**
 * Renders a content date exactly the way the visible review lines do:
 * "November 1, 2026" in English, "1 de noviembre de 2026" in Spanish.
 * Shared so the top-of-page line and the bottom notice can never disagree.
 */
export function formatReviewedDate(iso: string, lang: "en" | "es"): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1)).toLocaleDateString(
    lang === "es" ? "es-ES" : "en-US",
    { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" },
  );
}

/** Newest content date across a set of paths/dates, for sitemap-index entries. */
export function newestLastmod(dates: (string | null | undefined)[]): string | undefined {
  return dates.filter(Boolean).sort().pop() as string | undefined;
}

/** Newest content date for each child sitemap in the sitemap index. */
export const sitemapLastmods = {
  "/sitemap.xml": () =>
    newestLastmod([
      ...Object.entries(routes)
        .filter(([p]) => p !== "/es" && !p.startsWith("/es/"))
        .map(([, d]) => d),
      ...Object.values(posts),
    ]),
  "/sitemap-es.xml": () =>
    newestLastmod([
      ...Object.entries(routes)
        .filter(([p]) => p === "/es" || p.startsWith("/es/"))
        .map(([, d]) => d),
      libs.esStories,
      ...Object.values(esPosts),
    ]),
  "/sitemap-stories.xml": () =>
    newestLastmod([routes["/results/story"], routes["/es/antes-y-despues"], libs.stories, libs.esStories]),
} as const;
